/**
 * Size guide V2.0 — progressive enhancement for snippets/size-guide-modal.liquid
 * and snippets/product-size-guide.liquid. Everything here is additive: without
 * it the guide still opens (assets/main-product.js), the photos are still
 * swipeable (CSS scroll-snap) and the table is still readable.
 *
 * - Closing (button + backdrop) is bound here too, idempotently with
 *   main-product.js, so a page without the size-selector trigger still works.
 * - "Ver guía completa" (compact panel) opens the same dialog and returns the
 *   focus to the link when it closes.
 * - Mobile photo carousel: dots follow the scroll position, dots scroll to a
 *   photo, ArrowLeft/ArrowRight step through photos when the strip is focused.
 * - Tables that really overflow become keyboard-scrollable regions.
 */
(function initSizeGuideV2() {
  const dialog = document.querySelector('.size-guide');
  if (!dialog || dialog.dataset.sizeGuideReady) return;
  dialog.dataset.sizeGuideReady = 'true';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

  dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  // "Ver guía completa" links (the compact panel). Hidden in the markup until
  // we know there is a dialog to open.
  document.querySelectorAll('[data-size-guide-open]').forEach((opener) => {
    opener.hidden = false;
    opener.addEventListener('click', () => {
      if (dialog.open) return;
      dialog.showModal();
      const returnFocus = () => {
        dialog.removeEventListener('close', returnFocus);
        // After any other `close` listener (main-product.js focuses its own trigger).
        setTimeout(() => opener.focus(), 0);
      };
      dialog.addEventListener('close', returnFocus);
    });
  });

  // Photo carousel (only rendered when a guide has two photos).
  dialog.querySelectorAll('[data-size-guide-photos]').forEach((root) => {
    const track = root.querySelector('[data-size-guide-track]');
    const slides = Array.from(root.querySelectorAll('[data-size-guide-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-size-guide-dot]'));
    if (!track || slides.length < 2) return;

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const isCarousel = () => maxScroll() > 1;
    const currentIndex = () => {
      const max = maxScroll();
      if (!max) return 0;
      return Math.round((track.scrollLeft / max) * (slides.length - 1));
    };
    const goTo = (index) => {
      const clamped = Math.min(Math.max(index, 0), slides.length - 1);
      track.scrollTo({ left: (maxScroll() * clamped) / (slides.length - 1), behavior: scrollBehavior() });
    };

    let frame = 0;
    const syncDots = () => {
      frame = 0;
      const active = currentIndex();
      dots.forEach((dot, i) => {
        dot.classList.toggle('product-size-guide__dot--active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    };
    track.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = requestAnimationFrame(syncDots);
      },
      { passive: true }
    );

    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

    track.addEventListener('keydown', (event) => {
      if (!isCarousel()) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(currentIndex() + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(currentIndex() - 1);
      }
    });

    // The strip is only a carousel (and only worth a tab stop) below 750px.
    const label = root.getAttribute('aria-label');
    const syncFocusable = () => {
      if (isCarousel()) {
        track.tabIndex = 0;
        track.setAttribute('role', 'group');
        if (label) track.setAttribute('aria-label', label);
      } else {
        track.removeAttribute('tabindex');
        track.removeAttribute('role');
        track.removeAttribute('aria-label');
      }
      syncDots();
    };
    new ResizeObserver(syncFocusable).observe(track);
    syncFocusable();
  });

  // Wide tables scroll sideways inside their wrapper — make that reachable by keyboard.
  document.querySelectorAll('[data-size-guide-table-wrap]').forEach((wrap) => {
    const caption = wrap.querySelector('caption')?.textContent.trim();
    const sync = () => {
      if (wrap.scrollWidth > wrap.clientWidth + 1) {
        wrap.tabIndex = 0;
        wrap.setAttribute('role', 'region');
        if (caption) wrap.setAttribute('aria-label', caption);
      } else {
        wrap.removeAttribute('tabindex');
        wrap.removeAttribute('role');
        wrap.removeAttribute('aria-label');
      }
    };
    new ResizeObserver(sync).observe(wrap);
    sync();
  });
})();
